import React, { useState, useCallback, useMemo } from "react";
import * as yup from "yup";
import {
    Button,
    Box,
    makeStyles,
    Dialog,
    DialogContent,
    TextField,
    useMediaQuery,
    InputAdornment,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import theme from "../../../themes";
import { PhoneNumberFormatCustom } from "../../common/utilities/PhoneNumberFormat";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import IconButton from "@material-ui/core/IconButton";
import { useEnumStrings } from "../../enumStrings";
import LogoImage from "../../common/utilities/LogoImage";
import {
    ChamberMemberReturn,
    useSendLoginInfoEmailToChamberMemberMutation,
    useSetNewTemporaryChamberMemberPasswordMutation,
    useGetSampleLoginInfoEmailQuery,
    useUpdateChamberMemberMutation,
    useChamberMemberChangeAdminConsoleLoginEmailMutation,
    useGetAdminConsoleLoginEmailForChamberMemberQuery,
    LoginInfoEmailTypes,
} from "../../../graphql/types";
import { useFormik } from "formik";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CSVLink } from "react-csv";
import { CircularProgress } from "@material-ui/core";
import CommonTable, { HeadCell } from "../../common/CommonTable";
import EditBlackIcon from "../../../images/edit-black-icon.png";
import ResetPasswordIcon from "../../../images/resetfinal.png";
import TickCircleIcon from "../../../images/tick-circle.png";
import SendCredentialsIcon from "../../../images/sendcreds.png";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ResetDashboardPasswordDialog from "../../common/ResetDashboardPasswordDialog";
import SendLoginInformationModal from "../../common/SendLoginInformationModal";
import SuccessDialog from "../../common/SuccessDialog";
import { DateTime } from "luxon";
import { getPlainFailureReason } from "../../common/utilities/userFacingError";

const useStyles = makeStyles(() => ({
    // ─── Mobile card styles ─────────────────────────────────────────────────
    mobileList: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        padding: "0 0 16px 0",
    },
    mobileSearchBar: {
        backgroundColor: "#fff",
        borderRadius: "8px",
        width: "100%",
        marginBottom: "12px",
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            "& fieldset": { borderColor: "#000" },
            "&.Mui-focused fieldset": { borderColor: "#000", borderWidth: "2px" },
        },
    },
    credCard: {
        backgroundColor: "#fff",
        borderRadius: "12px",
        border: "1px solid #E8E8E8",
        padding: "14px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
    },
    credCardTop: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    credCardName: {
        fontWeight: 700,
        fontSize: "14px",
        color: "#111",
        flex: 1,
        lineHeight: 1.3,
    },
    // Column section: label above + content below
    credCardSection: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    credCardColLabel: {
        fontSize: "10px",
        color: "#888",
        fontWeight: 600,
        textTransform: "uppercase" as const,
        letterSpacing: "0.5px",
    },
    credCardBadge: {
        fontSize: "12px",
        fontWeight: 700,
        display: "inline-block",
        textTransform: "uppercase" as const,
        letterSpacing: "0.3px",
    },
    credBadgeGreen: {
        color: "#2E7D32",
    },
    credBadgeRed: {
        color: "#C62828",
    },
    credCardSectionInline: {
        display: "flex",
        flexDirection: "row" as const,
        alignItems: "baseline",
        gap: "6px",
        flexWrap: "wrap" as const,
    },
    credCardSendBtn: {
        width: "100%",
        fontWeight: 700,
        borderRadius: "8px",
        textTransform: "none" as const,
        fontSize: "13px",
        height: "42px",
        marginTop: "4px",
    },
    // ────────────────────────────────────────────────────────────────────────
    tableWrapper: {
        width: "100%",
        overflowX: "auto",
        "@media (max-width: 1500px)": {
            fontSize: "14px",
        },
        "@media (max-width: 1400px)": {
            fontSize: "13px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "12px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "11px",
        },
    },
    tableContainer: {
        boxShadow: "none",
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        border: "1px solid #E8E8E8",
    },
    tableHeaderCell: {
        fontWeight: 700,
        fontSize: "17px",
        color: "#000",
        backgroundColor: "#ffffff",
        padding: "16px",
        verticalAlign: "top",
        borderBottom: "1px solid #E8E8E8",
        cursor: "pointer",
        userSelect: "none",
        whiteSpace: "nowrap",
        "&:hover": {
            backgroundColor: "#f9f9f9",
        },
        "@media (max-width: 1400px)": {
            fontSize: "15px",
            padding: "12px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "14px",
            padding: "10px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "13px",
            padding: "8px",
        },
    },
    headerContent: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    sortIcon: {
        width: "16px",
        height: "16px",
        display: "inline-block",
    },
    tableBodyCell: {
        fontSize: "18px",
        fontWeight: 500,
        color: "#000",
        borderBottom: "1px solid #E8E8E8",
        padding: "16px",
        backgroundColor: "#ffffff",
        "@media (max-width: 1400px)": {
            fontSize: "16px",
            padding: "12px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "15px",
            padding: "10px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "14px",
            padding: "8px",
        },
    },
    tableBodyCellAlternating: {
        backgroundColor: "#F4F4F4",
    },
    companyName: {
        fontSize: "14px",
        color: "#000",
        fontWeight: 500,
        wordWrap: "break-word",
        overflowWrap: "break-word",
        lineHeight: "1.3",
        display: "block",
        "@media (max-width: 1500px)": {
            fontSize: "13px",
        },
        "@media (max-width: 1400px)": {
            fontSize: "12px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "11px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "10px",
        },
    },
    adminContactName: {
        fontWeight: "bold",
        fontSize: "17px",
        color: "#000",
        marginBottom: "4px",
        "@media (max-width: 1400px)": {
            fontSize: "15px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "14px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "13px",
        },
    },
    adminContactEmail: {
        fontSize: "17px",
        color: "#666",
        "@media (max-width: 1400px)": {
            fontSize: "15px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "14px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "13px",
        },
    },
    // Send button: orange pill for first-time send
    sendButton: {
        backgroundColor: theme.palette.primary.main,
        color: "white",
        fontWeight: "bold",
        borderRadius: "20px",
        textTransform: "none",
        padding: "6px 14px",
        fontSize: "10px",
        minWidth: "160px",
        whiteSpace: "nowrap",
        boxShadow: "none",
        "&:hover": {
            backgroundColor: "#D49820",
            boxShadow: "none",
        },
        "@media (max-width: 1500px)": {
            minWidth: "145px",
            fontSize: "9px",
            padding: "5px 12px",
        },
        "@media (max-width: 1400px)": {
            minWidth: "130px",
            fontSize: "9px",
            padding: "5px 10px",
        },
        "@media (max-width: 1200px)": {
            minWidth: "120px",
            fontSize: "8px",
            padding: "4px 8px",
        },
        "@media (max-width: 1024px)": {
            minWidth: "100px",
            fontSize: "8px",
            padding: "4px 6px",
        },
    },
    // Resend button: dark/black pill for resend
    resendButton: {
        backgroundColor: theme.palette.primary.main,
        color: "white",
        fontWeight: "bold",
        borderRadius: "20px",
        textTransform: "none",
        padding: "6px 14px",
        fontSize: "10px",
        minWidth: "160px",
        whiteSpace: "nowrap",
        boxShadow: "none",
        "&:hover": {
            backgroundColor: "#D49820",
            boxShadow: "none",
        },
        "@media (max-width: 1500px)": {
            minWidth: "145px",
            fontSize: "9px",
            padding: "5px 12px",
        },
        "@media (max-width: 1400px)": {
            minWidth: "130px",
            fontSize: "9px",
            padding: "5px 10px",
        },
        "@media (max-width: 1200px)": {
            minWidth: "120px",
            fontSize: "8px",
            padding: "4px 8px",
        },
        "@media (max-width: 1024px)": {
            minWidth: "100px",
            fontSize: "8px",
            padding: "4px 6px",
        },
    },
    // Status text: green for Accessed
    statusAccessed: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#2E7D32",
        "@media (max-width: 1500px)": {
            fontSize: "12px",
        },
        "@media (max-width: 1400px)": {
            fontSize: "11px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "10px",
        },
    },
    // Status text: red for Not Accessed
    statusNotAccessed: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#C62828",
        "@media (max-width: 1500px)": {
            fontSize: "12px",
        },
        "@media (max-width: 1400px)": {
            fontSize: "11px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "10px",
        },
    },
    credentialsSentText: {
        fontSize: "12px",
        color: "#000",
        fontWeight: 500,
        lineHeight: "1.4",
        "@media (max-width: 1500px)": {
            fontSize: "11px",
        },
        "@media (max-width: 1400px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "9px",
        },
    },
    credentialsSentTime: {
        fontSize: "12px",
        color: "#000",
        fontWeight: 500,
        "@media (max-width: 1500px)": {
            fontSize: "11px",
        },
        "@media (max-width: 1400px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "9px",
        },
    },
    credentialsSentPlaceholder: {
        fontSize: "12px",
        color: "#999",
        fontWeight: 500,
        "@media (max-width: 1500px)": {
            fontSize: "11px",
        },
        "@media (max-width: 1400px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "9px",
        },
    },
    editIconButton: {
        color: theme.palette.primary.main,
        padding: "8px",
        "&:hover": {
            backgroundColor: "rgba(225, 167, 49, 0.1)",
        },
    },
    alternatingRow: {
        backgroundColor: "#F4F4F4",
    },
    // Contact card: solid border rounded card
    contactBox: {
        border: "1.5px solid #000",
        borderRadius: "8px",
        padding: "8px 30px 8px 10px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        backgroundColor: "transparent",
        "@media (max-width: 1500px)": {
            padding: "7px 28px 7px 8px",
            gap: "3px",
            borderWidth: "1.5px",
        },
        "@media (max-width: 1400px)": {
            padding: "6px 26px 6px 7px",
            gap: "3px",
            borderWidth: "1px",
        },
        "@media (max-width: 1200px)": {
            padding: "5px 24px 5px 6px",
            gap: "2px",
            borderWidth: "1px",
        },
        "@media (max-width: 1024px)": {
            padding: "4px 22px 4px 5px",
            gap: "2px",
            borderWidth: "1px",
        },
        "@media (max-width: 600px)": {
            padding: "10px 52px 10px 12px",
            gap: "4px",
            borderWidth: "1px",
        },
    },
    contactInfo: {
        display: "flex",
        alignItems: "center",
        gap: "0px",
        fontSize: "12px",
        "@media (max-width: 1500px)": {
            fontSize: "11px",
        },
        "@media (max-width: 1400px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "9px",
        },
    },
    contactLabel: {
        fontWeight: 700,
        color: "#000",
        fontSize: "12px",
        "@media (max-width: 1500px)": {
            fontSize: "11px",
        },
        "@media (max-width: 1400px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "9px",
        },
    },
    contactValue: {
        fontWeight: 400,
        color: "#000",
        fontSize: "12px",
        marginLeft: "4px",
        "@media (max-width: 1500px)": {
            fontSize: "11px",
        },
        "@media (max-width: 1400px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1200px)": {
            fontSize: "10px",
        },
        "@media (max-width: 1024px)": {
            fontSize: "9px",
        },
    },
    contactIcon: {
        width: "18px",
        height: "18px",
        display: "inline-block",
        flexShrink: 0,
    },
    editBoxIcon: {
        position: "absolute" as const,
        top: "50%",
        right: "7px",
        transform: "translateY(-50%)",
        width: "26px",
        height: "23px",
        cursor: "pointer",
        padding: "3px 5px",
        borderRadius: "5px",
        border: "none",
        backgroundColor: "#000000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "@media (max-width: 1500px)": {
            width: "24px",
            height: "21px",
            right: "6px",
        },
        "@media (max-width: 1400px)": {
            width: "22px",
            height: "19px",
            right: "5px",
        },
        "@media (max-width: 1200px)": {
            width: "20px",
            height: "18px",
            right: "4px",
        },
        "@media (max-width: 1024px)": {
            width: "18px",
            height: "16px",
            right: "4px",
        },
        "@media (max-width: 600px)": {
            width: "36px",
            height: "36px",
            right: "8px",
            padding: "6px",
            borderRadius: "8px",
        },
    },
    credentialsWithIcon: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    dialogPaper: {
        borderRadius: "20px",
        padding: "0",
        maxWidth: "min(900px, 95vw)",
        width: "100%",
        maxHeight: "95vh",
        position: "relative",
        overflow: "hidden",
        margin: "20px",
    },
    dialogPaperSmall: {
        borderRadius: "20px",
        padding: "0",
        maxWidth: "520px",
        width: "100%",
        position: "relative",
        overflow: "visible",
        margin: "20px",
    },
    dialogCloseButton: {
        position: "absolute",
        right: "10px",
        top: "10px",
        backgroundColor: (theme.palette as any).delete.main,
        color: "#FFFFFF",
        width: "30px",
        height: "30px",
        border: "none",
        borderRadius: "50%",
        zIndex: 1,
        padding: "0",
        minWidth: "30px",
        "&:hover": {
            backgroundColor: "#DC2626",
        },
        "& svg": {
            fontSize: "18px",
            color: "#FFFFFF",
        },
    },
    dialogIconContainer: {
        display: "flex",
        justifyContent: "center",
        marginBottom: "20px",
        marginTop: "20px",
    },
    dialogIcon: {
        width: "80px",
        height: "80px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    dialogIconReset: {
        border: `3px solid ${theme.palette.primary.main}`,
    },
    dialogIconDelete: {
        border: "3px solid #EF4444",
        backgroundColor: "#FEE2E2",
    },
    dialogIconSuspend: {
        border: `3px solid ${theme.palette.primary.main}`,
    },
    dialogIconSuccess: {
        border: "3px solid #22C55E",
        backgroundColor: "#DCFCE7",
    },
    successDialogPaper: {
        borderRadius: "16px",
        padding: "40px",
        maxWidth: "500px",
        width: "500px",
        position: "relative",
    },
    successSection: {
        border: "2px dotted #E5E7EB",
        borderRadius: "12px",
        padding: "24px",
        backgroundColor: "#FEFCE8",
    },
    successSectionDark: {
        backgroundColor: "#F9FAFB",
    },
    successSectionTitle: {
        fontSize: "20px",
        fontWeight: "bold",
        marginBottom: "16px",
    },
    successSectionTitleBold: {
        fontSize: "25px",
        fontWeight: "bold",
    },
    successSectionTitleGold: {
        color: theme.palette.primary.main,
    },
    successSectionTitleBlack: {
        color: "#000",
    },
    successButtonOutline: {
        backgroundColor: "transparent",
        color: "#000",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "10px 32px",
        textTransform: "none",
        fontSize: "16px",
        border: "2px dotted #000",
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
        },
    },
    previewEmailDialogPaper: {
        borderRadius: "20px",
        padding: "0",
        width: "966px",
        maxWidth: "95vw",
        maxHeight: "95vh",
        position: "relative",
        background: "#D9D9D9",
        overflow: "hidden",
    },
    previewEmailHeader: {
        display: "flex",
        alignItems: "center",
        padding: "28px 30px 0 30px",
        marginBottom: "24px",
    },
    previewEmailTitle: {
        fontSize: "25px",
        fontWeight: 700,
        lineHeight: "200%",
        textAlign: "center",
        flex: 1,
        color: "#000000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    previewEmailContent: {
        border: "1px solid #000000",
        borderRadius: "16px",
        padding: "38px 20px",
        backgroundColor: "#FFFFFF",
        fontSize: "18px",
        fontWeight: 400,
        lineHeight: "200%",
        color: "#000000",
        margin: "0 30px 30px 30px",
        maxHeight: "calc(95vh - 150px)",
        overflowY: "auto",
    },
    backButton: {
        minWidth: "38px",
        width: "38px",
        height: "38px",
        padding: 0,
        borderRadius: "8px",
        "& svg": {
            fontSize: "24px",
            color: "#000000",
            strokeWidth: "3px",
        },
    },
    dialogTitle: {
        fontSize: "24px",
        fontWeight: "bold",
        textAlign: "center",
        marginBottom: "16px",
    },
    dialogDescription: {
        fontSize: "16px",
        color: "#666",
        textAlign: "center",
        marginBottom: "12px",
        lineHeight: "1.5",
    },
    dialogConfirmText: {
        fontSize: "16px",
        fontWeight: "bold",
        textAlign: "center",
        marginBottom: "24px",
        color: "#333",
    },
    dialogButtons: {
        display: "flex",
        gap: "16px",
        justifyContent: "center",
    },
    dialogButtonYes: {
        backgroundColor: theme.palette.primary.main,
        color: "white",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "12px 48px",
        textTransform: "none",
        fontSize: "16px",
        "&:hover": {
            backgroundColor: "#D4A353",
        },
    },
    dialogButtonNo: {
        backgroundColor: "#333",
        color: "white",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "12px 48px",
        textTransform: "none",
        fontSize: "16px",
        "&:hover": {
            backgroundColor: "#444",
        },
    },
    dialogButtonDelete: {
        backgroundColor: "#EF4444",
        color: "white",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "12px 48px",
        textTransform: "none",
        fontSize: "16px",
        "&:hover": {
            backgroundColor: "#DC2626",
        },
    },
    dialogButtonCancel: {
        backgroundColor: "#333",
        color: "white",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "12px 48px",
        textTransform: "none",
        fontSize: "16px",
        "&:hover": {
            backgroundColor: "#444",
        },
    },
    successButton: {
        backgroundColor: theme.palette.primary.main,
        color: "white",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "10px 32px",
        textTransform: "none",
        fontSize: "14px",
        "&:hover": {
            backgroundColor: "#D4A353",
        },
    },
    sendEmailButton: {
        backgroundColor: "#000",
        color: "white",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "10px 32px",
        textTransform: "none",
        fontSize: "14px",
        "&:hover": {
            backgroundColor: "#000",
        },
    },
    // New Send Login Credentials Dialog Styles
    sendCredentialsDialogContent: {
        padding: "0",
        overflowY: "auto",
        overflowX: "hidden",
        "&::-webkit-scrollbar": {
            width: "8px",
        },
        "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
        },
    },
    sendCredentialsTitle: {
        fontWeight: 700,
        fontSize: "20px",
        lineHeight: "1.5",
        textAlign: "center",
        color: "#000000",
        marginTop: "12px",
        marginBottom: "12px",
        "@media (max-width: 1200px)": {
            fontSize: "17px",
            marginTop: "10px",
            marginBottom: "10px",
        },
    },
    sendCredentialsPanelsContainer: {
        display: "flex",
        flexDirection: "row",
        gap: "20px",
        padding: "0 30px 30px 30px",
        "@media (max-width: 1200px)": {
            flexDirection: "column",
            padding: "0 16px 16px 16px",
            gap: "16px",
        },
    },
    sendCredentialsPanel: {
        flex: 1,
        minHeight: "340px",
        borderRadius: "16px",
        padding: "0 0 110px 0",
        position: "relative",
        "@media (max-width: 1200px)": {
            minHeight: "300px",
            padding: "0 0 100px 0",
        },
    },
    sendCredentialsPanelLeft: {
        border: `1px solid ${theme.palette.primary.main}`,
    },
    sendCredentialsPanelRight: {
        border: "1px solid #000000",
    },
    sendCredentialsPanelTitle: {
        fontWeight: 700,
        fontSize: "20px",
        lineHeight: "1.3",
        padding: "20px 18px 0 18px",
        marginBottom: "12px",
        "@media (max-width: 1200px)": {
            fontSize: "18px",
            padding: "16px 14px 0 14px",
            marginBottom: "10px",
        },
    },
    sendCredentialsPanelTitleGold: {
        color: theme.palette.primary.main,
    },
    sendCredentialsPanelTitleBlack: {
        color: "#000000",
    },
    sendCredentialsPanelDescription: {
        fontWeight: 400,
        fontSize: "14px",
        lineHeight: "1.5",
        padding: "0 18px",
        color: "#000000",
        "@media (max-width: 1200px)": {
            fontSize: "13px",
            padding: "0 14px",
            lineHeight: "1.4",
        },
    },
    sendCredentialsButtonsContainer: {
        position: "absolute",
        bottom: "16px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "90%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px",
        "@media (max-width: 1200px)": {
            gap: "8px",
            bottom: "14px",
        },
    },
    sendCredentialsPreviewButton: {
        width: "auto",
        minWidth: "150px",
        maxWidth: "90%",
        height: "38px",
        background: "#000000",
        border: "1px solid #000000",
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "14px",
        lineHeight: "1",
        textTransform: "uppercase",
        color: "#FFFFFF",
        padding: "8px 24px",
        whiteSpace: "nowrap",
        "&:hover": {
            background: "#333333",
        },
        "@media (max-width: 1200px)": {
            fontSize: "13px",
            height: "36px",
            padding: "8px 20px",
        },
    },
    sendCredentialsDownloadButton: {
        width: "auto",
        minWidth: "280px",
        maxWidth: "90%",
        height: "42px",
        background: theme.palette.primary.main,
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "16px",
        lineHeight: "1",
        textTransform: "uppercase",
        color: "#FFFFFF",
        padding: "10px 24px",
        whiteSpace: "nowrap",
        "&:hover": {
            background: "#D49820",
        },
        "@media (max-width: 1200px)": {
            fontSize: "14px",
            minWidth: "240px",
            height: "40px",
            padding: "10px 20px",
        },
    },
    sendCredentialsSendButton: {
        width: "auto",
        minWidth: "280px",
        maxWidth: "90%",
        height: "42px",
        background: theme.palette.success.main,
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "16px",
        lineHeight: "1",
        textTransform: "uppercase",
        color: "#FFFFFF",
        padding: "10px 24px",
        whiteSpace: "nowrap",
        "&:hover": {
            background: "#009B1C",
        },
        "@media (max-width: 1200px)": {
            fontSize: "14px",
            minWidth: "240px",
            height: "40px",
            padding: "10px 20px",
        },
    },
    // Contact Edit Dialog Styles
    editContactDialogPaper: {
        borderRadius: "24px",
        padding: "0",
        maxWidth: "800px",
        width: "100%",
        maxHeight: "95vh",
        position: "relative",
        overflow: "hidden",
        margin: "10px",
        backgroundColor: "#d9d9d9",
        [theme.breakpoints.down("sm")]: {
            borderRadius: "16px",
            margin: "12px",
            maxHeight: "calc(100vh - 24px)",
        },
    },
    editContactDialogContent: {
        padding: "16px 24px",
        overflowY: "auto",
        overflowX: "hidden",

        "&::-webkit-scrollbar": {
            width: "8px",
        },
        "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
        },
        [theme.breakpoints.down("sm")]: {
            padding: "12px 14px",
        },
    },
    editContactContentArea: {
        border: "1.5px solid #4A7DB5",
        borderRadius: "16px",
        padding: "16px 20px",
        backgroundColor: "#FFFFFF",
        [theme.breakpoints.down("sm")]: {
            padding: "12px 14px",
        },
    },
    editContactSectionHeader: {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "30px",
        padding: "8px 24px",
        display: "inline-block",
        position: "relative",
        top: "-18px",
        left: "0",
        marginBottom: "-10px",
        [theme.breakpoints.down("sm")]: {
            padding: "6px 14px",
            top: "-16px",
            maxWidth: "calc(100% - 8px)",
        },
    },
    editContactSectionTitle: {
        color: "#FFFFFF",
        fontSize: "15px",
        fontWeight: 700,
        margin: 0,
        lineHeight: "20px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "13px",
            lineHeight: "16px",
        },
    },
    editContactFormRow: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "20px",
        marginBottom: "20px",
        alignItems: "end",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
            gap: "14px",
            marginBottom: "14px",
        },
    },
    editContactFieldLabel: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "13px",
        color: "#000000",
        fontWeight: 600,
        marginBottom: "4px",
    },
    editContactRequiredAsterisk: {
        color: "#D32F2F",
        marginLeft: "2px",
        fontWeight: "bold",
    },
    editContactInput: {
        width: "100%",
        "& .MuiOutlinedInput-root": {
            "& fieldset": {
                borderColor: theme.palette.text.primary,
                borderRadius: "8px",
            },
            "&:hover fieldset": {
                borderColor: theme.palette.text.primary,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
            },
        },
        "& .MuiOutlinedInput-input": {
            padding: "10px 12px",
            fontSize: "13px",
        },
    },
    editContactUpdateButton: {
        backgroundColor: theme.palette.primary.main,
        color: "#FFFFFF",
        fontWeight: 700,
        borderRadius: "12px",
        padding: "10px 48px",
        textTransform: "none" as const,
        fontSize: "14px",
        "&:hover": {
            backgroundColor: "#D49820",
        },
    },
    editContactDialogTitle: {
        fontSize: "20px",
        fontWeight: 700,
        textAlign: "center" as const,
        color: "#000000",
        marginBottom: "12px",
    },
    notificationDialogPaper: {
        borderRadius: "16px",
        maxWidth: "380px",
        width: "380px",
        position: "relative" as const,
        overflow: "visible",
    },
    notificationIconCircle: {
        width: "60px",
        height: "60px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 16px auto",
    },
    notificationMessage: {
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "1.5",
        color: "#000000",
        textAlign: "center" as const,
    },
}));

/**
 * Helper to format credentials sent timestamp as:
 * Line 1: "Mon DD, YYYY"
 * Line 2: "HH:MMAM/PM"
 */
const formatCredentialsSentDate = (utcMilli?: number | null): { dateLine: string; timeLine: string } => {
    if (!utcMilli || utcMilli === 0) {
        return { dateLine: "", timeLine: "" };
    }
    const utcDateTime = DateTime.fromMillis(utcMilli, { zone: "utc" });
    const localDateTime = utcDateTime.toLocal();
    const dateLine = localDateTime.toFormat("LLL dd, yyyy");
    const timeLine = localDateTime.toFormat("hh:mma");
    return { dateLine, timeLine };
};

// EditContactForm sub-component with its own formik instance
export const EditContactForm: React.FC<{
    member: ChamberMemberReturn;
    classes: any;
    enumStrings: any;
    chamberInfo: any;
    updateChamberMember: any;
    onClose: (updated?: boolean, memberName?: string, memberEmail?: string) => void;
    onSaving: (saving: boolean) => void;
    isSaving: boolean;
    showNotification: (type: "success" | "error", message: string) => void;
}> = ({ member, classes, enumStrings, chamberInfo, updateChamberMember, onClose, onSaving, isSaving, showNotification }) => {
    // Keep refs to avoid stale closures in formik's cached onSubmit
    const onCloseRef = React.useRef(onClose);
    React.useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
    const onSavingRef = React.useRef(onSaving);
    React.useEffect(() => { onSavingRef.current = onSaving; }, [onSaving]);
    const showNotificationRef = React.useRef(showNotification);
    React.useEffect(() => { showNotificationRef.current = showNotification; }, [showNotification]);

    const [changeAdminEmail] = useChamberMemberChangeAdminConsoleLoginEmailMutation();

    // Fetch the ACTUAL login email from auth system (Cognito), not from DB record
    const { data: adminEmailData } = useGetAdminConsoleLoginEmailForChamberMemberQuery({
        variables: { chamberMemberId: member.id },
        fetchPolicy: "network-only",
    });

    const cognitoEmail = adminEmailData?.getAdminConsoleLoginEmailForChamberMember?.adminConsoleLoginEmail || "";

    // Use Cognito email as source of truth, fall back to DB email
    const resolvedEmail = cognitoEmail || member.adminConsoleLoginEmail || "";

    const validationSchema = yup.object({
        adminConsoleContactName: yup.string().required("Administrator Contact Name is required"),
        adminConsoleEmail: yup.string().email("Invalid email address").required("Administrator Contact Email is required"),
        adminConsolePhone: yup.string().required("Administrator Phone Number is required"),
    });

    const formik = useFormik({
        initialValues: {
            contactName: member.contactName || "",
            primaryContactTitle: "",
            adminConsoleLoginEmail: resolvedEmail,
            primaryContactPhone: "",
            adminConsoleContactName: member.adminConsoleContactName || "",
            adminConsoleContactTitle: member.cpaAdminCompanyAdminContactTitle || "",
            adminConsoleEmail: resolvedEmail,
            adminConsolePhone: member.cpaAdminCompanyAdminContactNumber || member.phoneNumber || "",
            adminConsolePhoneExtension: member.cpaAdminCompanyAdminContactNumberExtension || "",
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            if (!chamberInfo || !chamberInfo.id) {
                showNotificationRef.current("error", "Unable to update member. Please try again.");
                return;
            }

            onSavingRef.current(true);
            try {
                const newEmail = (values.adminConsoleEmail || "").trim().toLowerCase();
                // Use the Cognito email as the current email (source of truth for auth)
                const currentCognitoEmail = (cognitoEmail || member.adminConsoleLoginEmail || "").trim().toLowerCase();

                // If email changed, use the dedicated email change mutation (updates auth system)
                if (newEmail && newEmail !== currentCognitoEmail) {
                    const { data: emailChangeData } = await changeAdminEmail({
                        variables: {
                            chamberMemberId: member.id,
                            currentAdminConsoleLoginEmail: currentCognitoEmail,
                            newAdminConsoleLoginEmail: newEmail,
                        },
                        refetchQueries: ["getAdminConsoleLoginEmailForChamberMember"],
                        awaitRefetchQueries: true,
                    });

                    if (emailChangeData?.chamberMemberChangeAdminConsoleLoginEmail?.adminConsoleLoginEmailChangedSuccessfully === false) {
                        const failureReason = emailChangeData.chamberMemberChangeAdminConsoleLoginEmail.failureReason || "";
                        // Wave 2: never show the AWS code ("UsernameExistsException ...") or raw provider text.
                        showNotificationRef.current("error", failureReason.includes("UsernameExistsException")
                            ? enumStrings.userFacingErrors.loginEmailInUse
                            : getPlainFailureReason(failureReason) || enumStrings.userFacingErrors.loginEmailChangeFailed);
                        onSavingRef.current(false);
                        return;
                    }
                }

                const mutationInput: any = {
                    id: member.id,
                    chamberId: chamberInfo.id,
                    categoryId: member.categoryId,
                    name: member.name,
                    address: {
                        city: member.address?.city || "",
                        postalCode: member.address?.postalCode || "",
                        province: member.address?.province || "",
                        streetAddress: member.address?.streetAddress || "",
                    },
                    phoneNumber: member.phoneNumber || "",
                    phoneNumberExtension: member.phoneNumberExtension || "",
                    email: member.email || "",
                    showInApp: member.showInApp,
                    hideAddressFromDirectory: member.hideAddressFromDirectory ?? false,
                    contactName: member.contactName || "",
                    // Primary contact fields - preserve existing values
                    primaryContactName: member.primaryContactName || "",
                    primaryContactTitle: member.primaryContactTitle || "",
                    primaryContactEmail: member.primaryContactEmail || "",
                    primaryContactPhoneNumber: member.primaryContactPhoneNumber || "",
                    primaryContactPhoneNumberExtension: member.primaryContactPhoneNumberExtension || "",
                    // Admin contact fields - from the form
                    adminConsoleContactName: (values.adminConsoleContactName || "").trim(),
                    cpaAdminCompanyAdminContactTitle: (values.adminConsoleContactTitle || "").trim(),
                    cpaAdminCompanyAdminContactNumber: (values.adminConsolePhone || "").trim(),
                    cpaAdminCompanyAdminContactNumberExtension: (values.adminConsolePhoneExtension || "").trim(),
                    adminConsoleLoginEmail: newEmail,
                    // Employee adoption contact fields - preserve existing values
                    cpaEmployeeAdoptionContactName: member.cpaEmployeeAdoptionContactName || "",
                    cpaEmployeeAdoptionContactTitle: member.cpaEmployeeAdoptionContactTitle || "",
                    cpaEmployeeAdoptionContactEmail: member.cpaEmployeeAdoptionContactEmail || "",
                    cpaEmployeeAdoptionContactNumber: member.cpaEmployeeAdoptionContactNumber || "",
                    cpaEmployeeAdoptionContactNumberExtension: member.cpaEmployeeAdoptionContactNumberExtension || "",
                    description: member.description || "",
                    website: member.website || "",
                    logo: member.logo || "",
                    facebook: member.facebook || "",
                    instagram: member.instagram || "",
                    threads: member.threads || "",
                    linkedin: member.linkedin || "",
                    twitter: member.twitter || "",
                    tiktok: member.tiktok || "",
                    youtube: member.youtube || "",
                    googleReviewLink: member.googleReviewLink || "",
                    isSuspended: member.isSuspended || false,
                };

                // Remove __typename from nested objects
                if (mutationInput.address) {
                    delete mutationInput.address.__typename;
                }

                await updateChamberMember({
                    variables: {
                        id: member.id,
                        input: mutationInput,
                    },
                    refetchQueries: ["getAllChamberMembers", "getChamberMember"],
                });

                // Use ref to avoid stale closure - this ensures we call the latest onClose
                onCloseRef.current(
                    true,
                    (values.adminConsoleContactName || values.contactName || "").trim(),
                    newEmail
                );
            } catch (error) {
                console.error("Error updating contact:", error);
                showNotificationRef.current("error", "Failed to update contact. Please try again.");
            } finally {
                onSavingRef.current(false);
            }
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            {/* Chamber Perks App Company Administrator Contact Section */}
            <div style={{ marginTop: "20px", marginBottom: "40px", border: "1px solid #9E9E9E", borderRadius: "16px", padding: "0px 24px 24px 24px", position: "relative" }}>
                <div className={classes.editContactSectionHeader}>
                    <p className={classes.editContactSectionTitle}>
                        {enumStrings.createMemberForm?.chamberPerksAdminContact || "Chamber Perks App™ Company Administrator Contact"}
                    </p>
                </div>
                <div style={{ marginBottom: "20px" }}>
                    <p style={{ fontSize: "12px", lineHeight: "16px", color: "#000000", fontWeight: 400, marginBottom: "4px", marginTop: 0 }}>
                        {enumStrings.createMemberForm.adminContactAutoAssignDesc}
                    </p>
                </div>

                <div className={classes.editContactFormRow}>
                    <div>
                        <div className={classes.editContactFieldLabel}>
                            <span>{enumStrings.createMemberForm?.contactName || "Administrator Contact Name"}<span className={classes.editContactRequiredAsterisk}>*</span></span>
                        </div>
                        <TextField
                            type="text"
                            placeholder={enumStrings.createMemberForm?.placeholders?.enterContactName || "Enter Administrator Contact Name"}
                            className={classes.editContactInput}
                            variant="outlined"
                            inputProps={{ autoComplete: "new-password" }}
                            id="adminConsoleContactName"
                            name="adminConsoleContactName"
                            value={formik.values.adminConsoleContactName}
                            onChange={formik.handleChange}
                            error={formik.touched.adminConsoleContactName && Boolean(formik.errors.adminConsoleContactName)}
                            helperText={formik.touched.adminConsoleContactName && formik.errors.adminConsoleContactName}
                            InputLabelProps={{ shrink: false }}
                        />
                    </div>
                    <div>
                        <div className={classes.editContactFieldLabel}>
                            <span>{enumStrings.createMemberForm?.title || "Title"}</span>
                        </div>
                        <TextField
                            type="text"
                            placeholder={enumStrings.createMemberForm?.placeholders?.enterContactTitle || "Enter Contact Person's Title"}
                            className={classes.editContactInput}
                            variant="outlined"
                            inputProps={{ autoComplete: "new-password" }}
                            id="adminConsoleContactTitle"
                            name="adminConsoleContactTitle"
                            value={formik.values.adminConsoleContactTitle}
                            onChange={formik.handleChange}
                            InputLabelProps={{ shrink: false }}
                        />
                    </div>
                </div>

                <div className={classes.editContactFormRow}>
                    <div>
                        <div className={classes.editContactFieldLabel}>
                            <span>{enumStrings.createMemberForm?.contactEmail || "Administrator Contact Email"}<span className={classes.editContactRequiredAsterisk}>*</span></span>
                        </div>
                        <div style={{ fontSize: "12px", color: "#666666", marginBottom: "4px" }}>
                            {enumStrings.createMemberForm?.adminEmailNote || "Used as the NEW Member Dashboard Credential email username."}
                        </div>
                        <TextField
                            type="text"
                            placeholder={enumStrings.createMemberForm?.placeholders?.enterContactEmail || "Enter Administrator Contact Email"}
                            className={classes.editContactInput}
                            variant="outlined"
                            inputProps={{ autoComplete: "new-password" }}
                            id="adminConsoleEmail"
                            name="adminConsoleEmail"
                            value={formik.values.adminConsoleEmail}
                            onChange={formik.handleChange}
                            error={formik.touched.adminConsoleEmail && Boolean(formik.errors.adminConsoleEmail)}
                            helperText={formik.touched.adminConsoleEmail && formik.errors.adminConsoleEmail}
                            InputLabelProps={{ shrink: false }}
                        />
                    </div>
                    <div>
                        <div style={{ display: "flex", gap: "10px" }}>
                            <div className={classes.editContactFieldLabel} style={{ flex: 1 }}>
                                <span>{enumStrings.createMemberForm?.contactPhone || "Administrator Phone Number"}<span className={classes.editContactRequiredAsterisk}>*</span></span>
                            </div>
                            <div className={classes.editContactFieldLabel} style={{ width: "80px" }}>
                                <span>{enumStrings.createMemberForm?.extensionNo || "Ext."}</span>
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: "10px" }}>
                            <TextField
                                type="text"
                                placeholder={enumStrings.createMemberForm?.placeholders?.enterContactPhone || "Enter Administrator Phone Number"}
                                className={classes.editContactInput}
                                variant="outlined"
                                inputProps={{ autoComplete: "new-password" }}
                                id="adminConsolePhone"
                                name="adminConsolePhone"
                                value={formik.values.adminConsolePhone}
                                onChange={formik.handleChange}
                                error={formik.touched.adminConsolePhone && Boolean(formik.errors.adminConsolePhone)}
                                helperText={formik.touched.adminConsolePhone && formik.errors.adminConsolePhone}
                                InputLabelProps={{ shrink: false }}
                                InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                                style={{ flex: 1 }}
                            />
                            <TextField
                                type="text"
                                placeholder={enumStrings.createMemberForm?.extensionPlaceholder || "0000"}
                                className={classes.editContactInput}
                                variant="outlined"
                                inputProps={{ autoComplete: "new-password" }}
                                id="adminConsolePhoneExtension"
                                name="adminConsolePhoneExtension"
                                value={(formik.values as any).adminConsolePhoneExtension || ""}
                                onChange={formik.handleChange}
                                InputLabelProps={{ shrink: false }}
                                style={{ width: "80px" }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* UPDATE Button */}
            <div style={{ display: "flex", justifyContent: "center", marginTop: "12px" }}>
                <Button
                    type="submit"
                    className={classes.editContactUpdateButton}
                    disabled={isSaving}
                >
                    {isSaving ? <CircularProgress size={24} style={{ color: "white" }} /> : "UPDATE"}
                </Button>
            </div>
        </form>
    );
};

interface MemberCredentialsTableProps {
    data: ChamberMemberReturn[];
    onManageMember: (member: ChamberMemberReturn) => void;
    onOpenCredentialsModal?: (member: ChamberMemberReturn) => void;
}

const MemberCredentialsTable: React.FC<MemberCredentialsTableProps> = ({
    data,
    onManageMember,
    onOpenCredentialsModal,
}) => {
    const classes = useStyles();
    const enumStrings = useEnumStrings() as any;
    const isMobile = useMediaQuery("(max-width: 600px)");
    const [mobileSearch, setMobileSearch] = useState("");
    const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
    const [selectedMemberName, setSelectedMemberName] = useState<string>("");
    const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
    const [resetPasswordSuccessDialogOpen, setResetPasswordSuccessDialogOpen] = useState(false);
    const [previewEmailDialogOpen, setPreviewEmailDialogOpen] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [downloadingCredentials, setDownloadingCredentials] = useState(false);
    const [emailSentSuccess, setEmailSentSuccess] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [selectedMemberEmail, setSelectedMemberEmail] = useState("");
    const [selectedMemberHasCredentialsSent, setSelectedMemberHasCredentialsSent] = useState(false);
    const [resendConfirmDialogOpen, setResendConfirmDialogOpen] = useState(false);
    const [resendAccessedConfirmDialogOpen, setResendAccessedConfirmDialogOpen] = useState(false);
    const [selectedMemberHasNotAccessed, setSelectedMemberHasNotAccessed] = useState(false);
    const [selectedMemberCompanyName, setSelectedMemberCompanyName] = useState("");
    const [resetPasswordConfirmDialogOpen, setResetPasswordConfirmDialogOpen] = useState(false);
    const [sendCredentialsDialogOpen, setSendCredentialsDialogOpen] = useState(false);
    const [editContactDialogOpen, setEditContactDialogOpen] = useState(false);
    const [editContactMember, setEditContactMember] = useState<ChamberMemberReturn | null>(null);
    const [editContactSaving, setEditContactSaving] = useState(false);
    const [notificationDialog, setNotificationDialog] = useState<{ open: boolean; type: "success" | "error"; message: string }>({ open: false, type: "success", message: "" });

    const showNotification = (type: "success" | "error", message: string) => {
        setNotificationDialog({ open: true, type, message });
        setTimeout(() => {
            setNotificationDialog((prev) => ({ ...prev, open: false }));
        }, 5000);
    };
    const chamberInfo = useGetChamberFromUserIdInAuth();

    // Mutations for send email functionality
    const [sendLoginInfoEmailToChamberMember] = useSendLoginInfoEmailToChamberMemberMutation();
    const [setNewTemporaryChamberMemberPassword] = useSetNewTemporaryChamberMemberPasswordMutation();
    const [updateChamberMember] = useUpdateChamberMemberMutation();

    // Query for email preview
    const { data: sampleEmailData } = useGetSampleLoginInfoEmailQuery({
        variables: {
            type: LoginInfoEmailTypes.ResetPassword,
            chamberMemberId: selectedMemberId || "",
        },
        skip: !selectedMemberId,
    });

    // CSV data for download
    const csvData = selectedMemberEmail && newPassword ? [
        ["Email", "Password"],
        [selectedMemberEmail, newPassword],
    ] : [];

    const handleResetPassword = (memberId: string, memberName: string, memberEmail: string, hasCredentialsSent: boolean = false, hasNotAccessed: boolean = false, companyName: string = "") => {
        setSelectedMemberId(memberId);
        setSelectedMemberName(memberName);
        setSelectedMemberEmail(memberEmail);
        setSelectedMemberHasCredentialsSent(hasCredentialsSent);
        setSelectedMemberHasNotAccessed(hasNotAccessed);
        setSelectedMemberCompanyName(companyName);
        if (hasCredentialsSent && hasNotAccessed) {
            setResendConfirmDialogOpen(true);
        } else if (hasCredentialsSent && !hasNotAccessed) {
            setResendAccessedConfirmDialogOpen(true);
        } else {
            setSendCredentialsDialogOpen(true);
        }
    };

    const confirmResetPassword = async () => {
        if (!selectedMemberId || !selectedMemberEmail) return;

        try {
            // Generate new temporary password
            const { data } = await setNewTemporaryChamberMemberPassword({
                variables: {
                    adminConsoleLoginEmail: selectedMemberEmail,
                    chamberMemberId: selectedMemberId,
                },
            });

            const password = data?.setNewTemporaryChamberMemberPassword?.password || "";
            if (password) {
                setNewPassword(password);

                // Automatically send the email
                try {
                    const emailResult = await sendLoginInfoEmailToChamberMember({
                        variables: {
                            adminConsoleLoginEmail: selectedMemberEmail,
                            chamberMemberId: selectedMemberId,
                            type: LoginInfoEmailTypes.ResetPassword,
                        },
                        refetchQueries: ["getAllChamberMembers"],
                        awaitRefetchQueries: true,
                    });

                    if (emailResult?.data?.sendLoginInfoEmailToChamberMember?.loginInfoEmailSentSuccessfully) {
                        setEmailSentSuccess(true);
                        setResetPasswordDialogOpen(false);
                        setResetPasswordSuccessDialogOpen(true);
                    } else {
                        showNotification("error", "Failed to send email. Please try again.");
                    }
                } catch (emailError) {
                    console.error("Error sending email:", emailError);
                    showNotification("error", "Failed to send email. Please try again.");
                }
            } else {
                showNotification("error", "Failed to generate temporary password. Please try again.");
            }
        } catch (error) {
            console.error("Error resetting password:", error);
            showNotification("error", "Failed to reset password. Please try again.");
        }
    };

    // Handler for Send Email button
    const handleSendEmail = async () => {
        if (!selectedMemberId || !selectedMemberEmail) {
            showNotification("error", "No member selected");
            return;
        }

        setSendingEmail(true);
        try {
            // Generate a temporary password first before sending the email
            const { data: passwordData } = await setNewTemporaryChamberMemberPassword({
                variables: {
                    adminConsoleLoginEmail: selectedMemberEmail,
                    chamberMemberId: selectedMemberId,
                },
            });

            const password = passwordData?.setNewTemporaryChamberMemberPassword?.password || "";
            if (!password) {
                showNotification("error", "Failed to generate credentials. Please try again.");
                setSendingEmail(false);
                return;
            }

            setNewPassword(password);

            const { data } = await sendLoginInfoEmailToChamberMember({
                variables: {
                    adminConsoleLoginEmail: selectedMemberEmail,
                    chamberMemberId: selectedMemberId,
                    type: LoginInfoEmailTypes.ResetPassword,
                },
                refetchQueries: ["getAllChamberMembers"],
                awaitRefetchQueries: true,
            });

            if (data?.sendLoginInfoEmailToChamberMember?.loginInfoEmailSentSuccessfully) {
                setEmailSentSuccess(true);
                showNotification("success", "Email sent successfully!");
            } else {
                showNotification("error", "Failed to send email. Please try again.");
            }
        } catch (error) {
            console.error("Error sending email:", error);
            showNotification("error", "Failed to send email. Please try again.");
        } finally {
            setSendingEmail(false);
        }
    };

    // Handler for Download Credentials button
    const handleDownloadCredentials = () => {
        if (newPassword) {
            // Trigger CSV download
            setTimeout(() => {
                const csvDownloadButton = document.getElementById("member-credentials-csv-download");
                if (csvDownloadButton) {
                    csvDownloadButton.click();
                    showNotification("success", "Credentials downloaded!");
                }
            }, 100);
        } else {
            showNotification("error", "No credentials to download. Please try again.");
        }
    };

    const handleCloseSuccessDialog = () => {
        setResetPasswordSuccessDialogOpen(false);
        setSelectedMemberId(null);
        setSelectedMemberName("");
        setSelectedMemberEmail("");
        setNewPassword("");
        setEmailSentSuccess(false);
    };

    // Column configuration for CommonTable - matching Figma order
    const columns: HeadCell[] = [
        {
            id: "name",
            label: enumStrings.manageMemberCredentials.tableHeaders.memberCompany,
            description: enumStrings.manageMemberCredentials.tableHeaders.companyNameHelper,
            width: "22%",
        },
        {
            id: "adminConsoleContactName",
            label: enumStrings.manageMemberCredentials.tableHeaders.companyAdminContact,
            description: enumStrings.manageMemberCredentials.tableHeaders.adminContactHelper,
            sortable: false,
            width: "28%",
        },
        {
            id: "sendCredentials",
            label: enumStrings.manageMemberCredentials.tableHeaders.sendCredentials,
            description: enumStrings.manageMemberCredentials.tableHeaders.sendCredentialsHelper,
            alignCenter: true,
            headerAlignLeft: true,
            sortable: false,
            width: "20%",
        },
        {
            id: "hasSignedUp",
            label: enumStrings.manageMemberCredentials.tableHeaders.memberDashboardLoginStatus,
            description: enumStrings.manageMemberCredentials.tableHeaders.loginStatusHelper,
            width: "15%",
        },
        {
            id: "credentialsSentAtUtcMilli",
            label: enumStrings.manageMemberCredentials.tableHeaders.credentialsSent,
            description: enumStrings.manageMemberCredentials.tableHeaders.credentialsSentHelper,
            width: "15%",
        },
    ];

    // Custom cell renderer for the table
    const renderCell = useCallback(
        (column: HeadCell, member: ChamberMemberReturn) => {
            switch (column.id) {
                case "name":
                    return (
                        <Box display="flex" alignItems="center" style={{ maxWidth: "100%", overflow: "hidden" }}>
                            <Box flexShrink={0}>
                                <LogoImage
                                    logoSource={member.logo}
                                    size="normal"
                                    squared={true}
                                    chamberName={member.name}
                                />
                            </Box>
                            <Box ml={1} style={{ minWidth: 0, flex: 1 }}>
                                <span className={classes.companyName}>{member.name}</span>
                            </Box>
                        </Box>
                    );
                case "adminConsoleContactName":
                    return (
                        <div className={classes.contactBox}>
                            <div className={classes.contactInfo}>
                                <span className={classes.contactLabel}>
                                    {enumStrings.manageMemberCredentials.contactLabels.contactName}
                                </span>
                                <span className={classes.contactValue}>
                                    {member.adminConsoleContactName || "N/A"}
                                </span>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                <span className={classes.contactLabel}>
                                    {enumStrings.manageMemberCredentials.contactLabels.usernameEmail}
                                </span>
                                <span className={classes.contactValue} style={{ marginLeft: 0 }}>
                                    {member.adminConsoleLoginEmail || "N/A"}
                                </span>
                            </div>
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditContactMember(member);
                                    setEditContactDialogOpen(true);
                                }}
                                className={classes.editBoxIcon}
                            >
                                <img
                                    src={EditBlackIcon}
                                    alt="Edit"
                                    style={{ width: "16px", height: "16px", filter: "brightness(0) invert(1)" }}
                                />
                            </div>
                        </div>
                    );
                case "hasSignedUp":
                    if (member.hasSignedUp) {
                        const { dateLine } = formatCredentialsSentDate(
                            member.firstLoginAfterSettingNewPermanentPasswordUtcMilli || 0
                        );
                        return (
                            <div>
                                <div className={classes.statusAccessed}>
                                    {enumStrings.manageMemberCredentials.chips.loggedIn}
                                </div>
                                {dateLine && (
                                    <div className={classes.credentialsSentText}>{dateLine}</div>
                                )}
                            </div>
                        );
                    }
                    return (
                        <span className={classes.statusNotAccessed}>
                            {enumStrings.manageMemberCredentials.chips.neverLoggedIn}
                        </span>
                    );
                case "sendCredentials":
                    return (
                        <Button
                            className={
                                member.credentialsSentAtUtcMilli
                                    ? classes.resendButton
                                    : classes.sendButton
                            }
                            variant="contained"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleResetPassword(member.id, member.adminConsoleContactName || member.contactName || "", member.adminConsoleLoginEmail || "", !!member.credentialsSentAtUtcMilli || !!member.hasSignedUp, !member.hasSignedUp, member.name || "");
                            }}
                        >
                            {enumStrings.manageMemberCredentials.tableHeaders.sendCredentials}
                        </Button>
                    );
                case "credentialsSentAtUtcMilli":
                    if (member.credentialsSentAtUtcMilli) {
                        const { dateLine, timeLine } = formatCredentialsSentDate(member.credentialsSentAtUtcMilli);
                        return (
                            <div>
                                <div className={classes.credentialsSentText}>{dateLine}</div>
                                <div className={classes.credentialsSentTime}>{timeLine}</div>
                            </div>
                        );
                    }
                    return (
                        <span className={classes.credentialsSentPlaceholder}>--/--/----</span>
                    );
                default:
                    return null;
            }
        },
        [classes, enumStrings]
    );

    const mobileFilteredData = useMemo(() => {
        const lower = mobileSearch.toLowerCase();
        if (!lower) return data;
        return data.filter((m) =>
            (m.name || "").toLowerCase().includes(lower) ||
            (m.adminConsoleContactName || "").toLowerCase().includes(lower)
        );
    }, [data, mobileSearch]);

    return (
        <div className={classes.tableWrapper}>
            {isMobile ? (
                <div className={classes.mobileList}>
                    <TextField
                        className={classes.mobileSearchBar}
                        variant="outlined"
                        size="small"
                        placeholder={(enumStrings as any).memberCredentialsTable?.searchPlaceholder || "Search members..."}
                        value={mobileSearch}
                        onChange={(e) => setMobileSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: "#666", fontSize: "18px" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {mobileFilteredData.map((member) => {
                        const hasCredentials = !!member.credentialsSentAtUtcMilli || !!member.hasSignedUp;
                        const hasNotAccessed = !member.hasSignedUp;
                        const { dateLine, timeLine } = formatCredentialsSentDate(member.credentialsSentAtUtcMilli);
                        const sendBtnLabel = member.credentialsSentAtUtcMilli
                            ? enumStrings.manageMemberCredentials?.chips?.resend || "Resend Credentials"
                            : enumStrings.manageMemberCredentials?.chips?.send || "Send Login Credentials";
                        return (
                            <div key={member.id} className={classes.credCard}>
                                {/* Column 1: Member Company — logo + name */}
                                <div className={classes.credCardTop}>
                                    <LogoImage logoSource={member.logo} size="normal" squared chamberName={member.name} />
                                    <div className={classes.credCardName}>{member.name}</div>
                                </div>

                                {/* Column 2: Company Admin Contact — same bordered box as desktop */}
                                <div className={classes.credCardSection}>
                                    <span className={classes.credCardColLabel}>
                                        {enumStrings.manageMemberCredentials?.tableHeaders?.companyAdminContact || "Company Admin Contact"}
                                    </span>
                                    <div className={classes.contactBox}>
                                        <div className={classes.contactInfo}>
                                            <span className={classes.contactLabel}>
                                                {enumStrings.manageMemberCredentials?.contactLabels?.contactName || "Name:"}
                                            </span>
                                            <span className={classes.contactValue}>
                                                {member.adminConsoleContactName || "N/A"}
                                            </span>
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                            <span className={classes.contactLabel}>
                                                {enumStrings.manageMemberCredentials?.contactLabels?.usernameEmail || "Username/Email:"}
                                            </span>
                                            <span className={classes.contactValue} style={{ marginLeft: 0 }}>
                                                {member.adminConsoleLoginEmail || "N/A"}
                                            </span>
                                        </div>
                                        <div
                                            onClick={() => {
                                                setEditContactMember(member);
                                                setEditContactDialogOpen(true);
                                            }}
                                            className={classes.editBoxIcon}
                                        >
                                            <img
                                                src={EditBlackIcon}
                                                alt="Edit"
                                                style={{ width: "16px", height: "16px", filter: "brightness(0) invert(1)" }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Column 3: Login Status */}
                                <div className={classes.credCardSectionInline}>
                                    <span className={classes.credCardColLabel}>
                                        {enumStrings.manageMemberCredentials?.tableHeaders?.memberDashboardLoginStatus || "Login Status"}:
                                    </span>
                                    <span className={`${classes.credCardBadge} ${member.hasSignedUp ? classes.credBadgeGreen : classes.credBadgeRed}`}>
                                        {member.hasSignedUp
                                            ? enumStrings.manageMemberCredentials?.chips?.loggedIn || "Accessed"
                                            : enumStrings.manageMemberCredentials?.chips?.neverLoggedIn || "Never Accessed"}
                                    </span>
                                </div>

                                {/* Column 4: Credentials Sent */}
                                <div className={classes.credCardSectionInline}>
                                    <span className={classes.credCardColLabel}>
                                        {enumStrings.manageMemberCredentials?.tableHeaders?.credentialsSent || "Credentials Sent"}:
                                    </span>
                                    {member.credentialsSentAtUtcMilli ? (
                                        <span>
                                            <span className={classes.credentialsSentText}>{dateLine}</span>
                                            {timeLine ? <span className={classes.credentialsSentTime} style={{ marginLeft: "6px" }}>{timeLine}</span> : null}
                                        </span>
                                    ) : (
                                        <span className={classes.credentialsSentPlaceholder}>--/--/----</span>
                                    )}
                                </div>

                                {/* Column 5: Send Credentials button */}
                                <Button
                                    className={classes.credCardSendBtn}
                                    variant="contained"
                                    style={{ backgroundColor: theme.palette.primary.main, color: "#fff" }}
                                    onClick={() =>
                                        handleResetPassword(
                                            member.id,
                                            member.adminConsoleContactName || member.contactName || "",
                                            member.adminConsoleLoginEmail || "",
                                            hasCredentials,
                                            hasNotAccessed,
                                            member.name || ""
                                        )
                                    }
                                >
                                    {sendBtnLabel}
                                </Button>
                            </div>
                        );
                    })}
                </div>
            ) : (
            <CommonTable<ChamberMemberReturn>
                data={data}
                columns={columns}
                defaultSortColumn="name"
                defaultSortOrder="asc"
                hideSearch={true}
                rowKeyField="id"
                renderCell={renderCell}
            />
            )}
            {/* Reset Dashboard Password Confirmation Dialog (first-time send) */}
            <ResetDashboardPasswordDialog
                open={resetPasswordConfirmDialogOpen}
                onClose={() => setResetPasswordConfirmDialogOpen(false)}
                onSendEmail={() => {
                    setResetPasswordConfirmDialogOpen(false);
                    setResetPasswordSuccessDialogOpen(true);
                }}
                onPreviewEmail={() => {
                    setResetPasswordConfirmDialogOpen(false);
                    setResetPasswordSuccessDialogOpen(true);
                }}
                onEdit={() => {
                    setResetPasswordConfirmDialogOpen(false);
                    const member = data.find((m) => m.id === selectedMemberId);
                    if (member) {
                        setEditContactMember(member);
                        setEditContactDialogOpen(true);
                    }
                }}
                loading={false}
                contactName={selectedMemberName || ""}
                contactEmail={selectedMemberEmail || ""}
            />

            {/* Resend Credentials Confirmation Dialog (credentials sent but not accessed) */}
            <ResetDashboardPasswordDialog
                open={resendConfirmDialogOpen}
                onClose={() => setResendConfirmDialogOpen(false)}
                onSendEmail={() => {
                    setResendConfirmDialogOpen(false);
                    setSendCredentialsDialogOpen(true);
                }}
                onPreviewEmail={() => {}}
                onEdit={() => {
                    setResendConfirmDialogOpen(false);
                    const member = data.find((m) => m.id === selectedMemberId);
                    if (member) {
                        setEditContactMember(member);
                        setEditContactDialogOpen(true);
                    }
                }}
                loading={false}
                contactName={selectedMemberName || ""}
                contactEmail={selectedMemberEmail || ""}
                variant="resend"
                memberCompanyName={selectedMemberCompanyName}
            />

            {/* Confirm Sending Credentials Dialog (credentials sent and accessed) */}
            <ResetDashboardPasswordDialog
                open={resendAccessedConfirmDialogOpen}
                onClose={() => setResendAccessedConfirmDialogOpen(false)}
                onSendEmail={() => {}}
                onPreviewEmail={() => {}}
                loading={false}
                contactName={selectedMemberName || ""}
                contactEmail={selectedMemberEmail || ""}
                variant="accessed"
                memberCompanyName={selectedMemberCompanyName}
            />

            {/* New Temporary Password Generated Dialog */}
            <SendLoginInformationModal
                open={resetPasswordSuccessDialogOpen}
                handleClose={handleCloseSuccessDialog}
                memberCompanyName={selectedMemberName}
                memberAdminPortalEmail={selectedMemberEmail}
                chamberMemberId={selectedMemberId}
                type="resetPassword"
                isSuspended={false}
            />

            {/* Send Login Credentials Dialog - full two-panel design */}
            <SendLoginInformationModal
                open={sendCredentialsDialogOpen}
                handleClose={() => {
                    setSendCredentialsDialogOpen(false);
                    setSelectedMemberId(null);
                    setSelectedMemberName("");
                    setSelectedMemberEmail("");
                }}
                memberCompanyName={selectedMemberName}
                memberAdminPortalEmail={selectedMemberEmail}
                chamberMemberId={selectedMemberId}
                type="memberTable"
                isSuspended={false}
            />

            {/* Reset Password confirmation Dialog */}
            <ResetDashboardPasswordDialog
                open={resetPasswordDialogOpen}
                onClose={() => setResetPasswordDialogOpen(false)}
                onSendEmail={handleSendEmail}
                onPreviewEmail={() => setPreviewEmailDialogOpen(true)}
                onEdit={() => {
                    setResetPasswordDialogOpen(false);
                    const member = data.find((m) => m.id === selectedMemberId);
                    if (member) {
                        setEditContactMember(member);
                        setEditContactDialogOpen(true);
                    }
                }}
                loading={sendingEmail}
                contactName={selectedMemberName}
                contactEmail={selectedMemberEmail}
                title={enumStrings.dialogs.sendLoginCredentialsTitle}
                description={enumStrings.dialogs.sendLoginCredentialsDesc}
            />

            {/* Preview Email and Success are now handled by SendLoginInformationModal above */}

            {/* Edit Contact Dialog */}
            <Dialog
                open={editContactDialogOpen}
                onClose={() => {
                    setEditContactDialogOpen(false);
                    setEditContactMember(null);
                }}
                classes={{ paper: classes.editContactDialogPaper }}
                maxWidth={false}
            >
                <IconButton
                    className={classes.dialogCloseButton}
                    onClick={() => {
                        setEditContactDialogOpen(false);
                        setEditContactMember(null);
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent className={classes.editContactDialogContent}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                        <IconButton
                            className={classes.backButton}
                            onClick={() => {
                                setEditContactDialogOpen(false);
                                setEditContactMember(null);
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <div style={{ flex: 1 }} />
                        <div style={{ width: "40px" }} />
                    </div>

                    <div className={classes.editContactDialogTitle}>
                        {enumStrings.dialogs.editAdministratorContact}
                    </div>

                    <div className={classes.editContactContentArea}>
                        {editContactMember && (
                            <EditContactForm
                                member={editContactMember}
                                classes={classes}
                                enumStrings={enumStrings}
                                chamberInfo={chamberInfo}
                                updateChamberMember={updateChamberMember}
                                onClose={(updated, memberName, memberEmail) => {
                                    if (updated && editContactMember) {
                                        // Set up send credentials dialog before closing edit dialog
                                        setSelectedMemberId(editContactMember.id);
                                        setSelectedMemberName(memberName || editContactMember.adminConsoleContactName || "");
                                        setSelectedMemberEmail(memberEmail || editContactMember.adminConsoleLoginEmail || "");
                                        setSelectedMemberHasCredentialsSent(!!editContactMember.credentialsSentAtUtcMilli);
                                        setSendCredentialsDialogOpen(true);
                                    }
                                    setEditContactDialogOpen(false);
                                    setEditContactMember(null);
                                }}
                                onSaving={setEditContactSaving}
                                isSaving={editContactSaving}
                                showNotification={showNotification}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Notification Dialog */}
            <SuccessDialog
                open={notificationDialog.open}
                onClose={() => setNotificationDialog((prev) => ({ ...prev, open: false }))}
                message={notificationDialog.message}
                variant={notificationDialog.type}
            />

            {/* Hidden CSV download link */}
            <CSVLink
                data={csvData}
                filename={`${selectedMemberName || "member"} dashboard credentials.csv`}
                id="member-credentials-csv-download"
                style={{ display: "none" }}
            />
        </div>

    );
};

export default MemberCredentialsTable;
